<!-- src/views/VenueAudit.vue -->
<template>
  <div class="container blue-grey-text text-lighten-1 venue-audit coredeck-page">
    <form class="card" @submit.prevent="onSubmit">
      <AuditScoreSummary
        title="Venue Audit"
        subtitle="Complete each section, then submit your audit."
        :meta="auditMeta"
        :venueScore="venueScore"
        :adminScore="adminScore"
        :answeredCount="answeredCount"
        :totalCount="totalCount"
        :message="auditMessage"
      />

      <div class="card-content brown lighten-5 blue-grey-text text-darken-4">
        <div
          v-if="!activeVenueId"
          class="card-panel amber lighten-5 amber-text text-darken-4"
          style="border-radius: 14px"
        >
          No venue selected. Select a venue first, then try again.
        </div>

        <div
          v-else-if="!activeAuditConfig"
          class="card-panel red lighten-5 red-text text-darken-3"
          style="border-radius: 14px"
        >
          No venue audit question set has been configured for
          <strong>{{ activeVenueId }}</strong
          >.
        </div>

        <template v-else>
          <div class="card-panel" style="border-radius: 14px; margin-top: 12px">
            <div class="row" style="margin-bottom: 0">
              <div class="col s12">
                <div class="blue-grey-text text-darken-3" style="font-weight: 700">
                  Closed days (saved per week)
                </div>

                <div
                  class="blue-grey-text text-darken-1"
                  style="font-size: 12px; margin-bottom: 10px"
                >
                  Set these once for the week. After your first Submit, they are saved and
                  auto-applied for the remainder of the week.
                </div>

                <div
                  v-if="closedDaysLocked"
                  class="chip green lighten-5 green-text text-darken-3"
                >
                  Closed days saved for this week
                </div>
              </div>

              <div class="col s12">
                <div class="closed-days-row">
                  <label
                    v-for="d in WEEKDAYS"
                    :key="d.key"
                    class="closed-day-chip"
                    :class="{ disabled: closedDaysLocked }"
                  >
                    <input
                      type="checkbox"
                      v-model="closedDays[d.key]"
                      :disabled="closedDaysLocked"
                    />
                    <span>{{ d.label }}</span>
                  </label>
                </div>

                <div
                  v-if="closedDaysLocked"
                  class="blue-grey-text text-darken-1"
                  style="font-size: 12px; margin-top: 8px"
                >
                  To change closed days after saving, you will need an admin edit flow.
                </div>
              </div>
            </div>
          </div>

          <ul class="collapsible" ref="collapsibleEl">
            <li v-for="sec in sectionList" :key="sec.id">
              <div class="collapsible-header">
                <i class="material-icons blue-grey-text text-darken-2">
                  {{ sec.icon }}
                </i>

                <span style="font-weight: 700">
                  {{ sec.title }}
                </span>

                <span class="grey-text text-darken-1 section-progress-text">
                  {{ getSectionAnsweredCount(sec.items) }}/{{ sec.items.length }} answered
                  · {{ getSectionPercent(sec.items) }}%
                </span>

                <span
                  class="new badge blue-grey lighten-4 blue-grey-text text-darken-4"
                  data-badge-caption="%"
                  style="margin-left: auto"
                >
                  {{ getSectionPercent(sec.items) }}
                </span>
              </div>

              <div class="collapsible-body">
                <SectionBlock :title="sec.title" :items="sec.items" v-model="answers" />
              </div>
            </li>
          </ul>

          <div class="row" style="margin-top: 24px; margin-bottom: 0">
            <div class="col s12 center-align">
              <button
                class="btn brand waves-effect waves-light"
                type="submit"
                :disabled="submitting || !canSubmit"
              >
                {{ submitting ? "Submitting..." : "Submit Audit" }}
                <i class="material-icons right">
                  {{ submitting ? "hourglass_top" : "send" }}
                </i>
              </button>
            </div>
          </div>

          <div class="results-wrap" v-show="showResults">
            <table class="striped centered">
              <thead>
                <tr>
                  <th>Score Type</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Venue Audit Score</strong></td>
                  <td>
                    <strong>{{ venueScore }}%</strong>
                  </td>
                </tr>
                <tr>
                  <td><strong>Admin Audit Score</strong></td>
                  <td>
                    <strong>{{ adminScore }}%</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div
          v-if="errorMsg"
          class="card-panel red lighten-5 red-text text-darken-3"
          style="margin-top: 12px"
        >
          {{ errorMsg }}
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  unref,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import SectionBlock from "@/components/SectionBlock.vue";
import AuditScoreSummary from "@/components/AuditScoreSummary.vue";
import { auth, db } from "@/firebase";
import { doc, getDoc, runTransaction, serverTimestamp } from "firebase/firestore";
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";
import { getAuditConfigForVenue } from "@/scripts/audits/venueAuditConfigs";
import {
  answeredCount as getAnsweredCount,
  buildInitialAnswers,
  getAuditKeys,
  scorePercent,
  sectionAnsweredCount,
  sectionPercent,
} from "@/scripts/audits/auditScoring";

/* -------------------------
   VENUE ID SOURCE OF TRUTH
------------------------- */
const route = useRoute();
const injectedVenueId = inject("selectedVenueId", null);

let venueContext = {};
try {
  venueContext = useVenueContext();
} catch (e) {
  console.warn("[AUDIT] useVenueContext unavailable:", e);
}

const contextVenueId = computed(() => {
  const direct =
    unref(venueContext?.venueId) ||
    unref(venueContext?.selectedVenueId) ||
    unref(venueContext?.currentVenueId) ||
    "";

  return String(direct || "").trim();
});

function readInjectedVenueId() {
  if (!injectedVenueId) return "";

  try {
    return String(unref(injectedVenueId) || "").trim();
  } catch {
    return "";
  }
}

function readFromStorage() {
  const keys = [
    "coredeck_venue",
    "selectedVenueId",
    "selected_venue_id",
    "venueId",
    "venue_id",
    "coredeck:selectedVenueId",
    "coredeck:selected_venue_id",
    "coredeck:venueId",
    "coredeck:venue_id",
    "currentVenueId",
    "current_venue_id",
  ];

  for (const key of keys) {
    const localValue = (localStorage.getItem(key) || "").trim();
    if (localValue) return localValue;

    const sessionValue = (sessionStorage.getItem(key) || "").trim();
    if (sessionValue) return sessionValue;
  }

  return "";
}

function resolveVenueId() {
  const fromContext = contextVenueId.value;
  if (fromContext) return fromContext;

  const fromRoute =
    String(route.params?.venueId ?? "").trim() ||
    String(route.params?.venue_id ?? "").trim() ||
    String(route.params?.id ?? "").trim();

  if (fromRoute) return fromRoute;

  const fromInject = readInjectedVenueId();
  if (fromInject) return fromInject;

  const fromStorage = readFromStorage();
  if (fromStorage) return fromStorage;

  return "";
}

const activeVenueId = computed(() => resolveVenueId());

/* -------------------------
   ACTIVE AUDIT CONFIG
------------------------- */
const activeAuditConfig = computed(() => {
  if (!activeVenueId.value) return null;
  return getAuditConfigForVenue(activeVenueId.value);
});

const sectionList = computed(() => activeAuditConfig.value?.sectionList || []);

const auditKeys = computed(() => getAuditKeys(sectionList.value));
const venueKeys = computed(() => auditKeys.value.venueKeys);
const adminKeys = computed(() => auditKeys.value.adminKeys);
const allKeys = computed(() => auditKeys.value.allKeys);

const auditMeta = computed(() => {
  if (!activeVenueId.value) return "";
  if (!activeAuditConfig.value) return `Venue: ${activeVenueId.value}`;

  return `${activeAuditConfig.value.label} · ${activeAuditConfig.value.id}`;
});

const auditMessage = computed(() => {
  if (!activeAuditConfig.value) return "";
  return "This audit uses the question set configured for the selected venue.";
});

/* -------------------------
   STATE
------------------------- */
const submitting = ref(false);
const showResults = ref(false);
const errorMsg = ref("");
const answers = ref({});

const canSubmit = computed(() => {
  return Boolean(activeVenueId.value && activeAuditConfig.value && allKeys.value.length);
});

/* -------------------------
   CLOSED DAYS
------------------------- */
const WEEKDAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

function buildEmptyClosedDays() {
  return WEEKDAYS.reduce((acc, day) => {
    acc[day.key] = false;
    return acc;
  }, {});
}

const closedDays = ref(buildEmptyClosedDays());
const closedDaysLocked = ref(false);

function toLondonYmd(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function getLondonWeekStartYmd(date = new Date()) {
  const ymd = toLondonYmd(date);
  const [year, month, day] = ymd.split("-").map(Number);
  const base = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const jsDay = base.getUTCDay();
  const delta = (jsDay + 6) % 7;

  base.setUTCDate(base.getUTCDate() - delta);

  return toLondonYmd(base);
}

function applyClosedDays(obj) {
  closedDays.value = buildEmptyClosedDays();

  if (!obj) return;

  for (const day of WEEKDAYS) {
    closedDays.value[day.key] = obj[day.key] === true;
  }
}

async function loadWeeklyClosedDaysIfExists(venueId) {
  if (!venueId) return;

  const weekStartKey = getLondonWeekStartYmd(new Date());
  const weekDocRef = doc(db, "venues", venueId, "auditWeeks", weekStartKey);

  const snap = await getDoc(weekDocRef);

  if (snap.exists()) {
    const data = snap.data() || {};
    applyClosedDays(data.closedDays || null);
    closedDaysLocked.value = true;
  } else {
    closedDays.value = buildEmptyClosedDays();
    closedDaysLocked.value = false;
  }
}

/* -------------------------
   SCORING
------------------------- */
const venueScore = computed(() => scorePercent(venueKeys.value, answers.value));
const adminScore = computed(() => scorePercent(adminKeys.value, answers.value));
const totalCount = computed(() => allKeys.value.length);

const answeredCount = computed(() => {
  return getAnsweredCount(allKeys.value, answers.value);
});

function getSectionAnsweredCount(items = []) {
  return sectionAnsweredCount(items, answers.value);
}

function getSectionPercent(items = []) {
  return sectionPercent(items, answers.value);
}

/* -------------------------
   MATERIALIZE COLLAPSIBLE
------------------------- */
const collapsibleEl = ref(null);
let instance = null;

function initCollapsible() {
  const M = window.M;
  if (!M || !collapsibleEl.value) return;

  instance?.destroy();
  instance = M.Collapsible.init(collapsibleEl.value, {
    accordion: true,
  });
}

/* -------------------------
   WATCH SELECTED VENUE / QUESTION SET
------------------------- */
watch(
  () => activeVenueId.value,
  async (venueId) => {
    errorMsg.value = "";
    showResults.value = false;
    answers.value = buildInitialAnswers(allKeys.value);

    if (!venueId) {
      errorMsg.value = "No venue selected. Select a venue first, then try again.";
      return;
    }

    if (!activeAuditConfig.value) {
      errorMsg.value = `No venue audit question set has been configured for ${venueId}.`;
      return;
    }

    try {
      await loadWeeklyClosedDaysIfExists(venueId);
    } catch (e) {
      console.warn("[AUDIT] weekly closedDays load failed:", e);
    }

    await nextTick();
    initCollapsible();
  },
  { immediate: true }
);

watch(
  () => allKeys.value.join("|"),
  async () => {
    answers.value = buildInitialAnswers(allKeys.value);

    await nextTick();
    initCollapsible();
  }
);

/* -------------------------
   INIT / DESTROY
------------------------- */
onMounted(async () => {
  await nextTick();
  initCollapsible();
});

onBeforeUnmount(() => {
  instance?.destroy();
});

/* -------------------------
   SUBMIT
------------------------- */
async function onSubmit() {
  submitting.value = true;
  errorMsg.value = "";
  showResults.value = false;

  try {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated.");

    const venueId = activeVenueId.value;
    if (!venueId) {
      throw new Error("No venue selected. Select a venue first, then submit.");
    }

    if (!activeAuditConfig.value) {
      throw new Error(`No venue audit question set has been configured for ${venueId}.`);
    }

    const dayKey = toLondonYmd(new Date());
    const weekStartKey = getLondonWeekStartYmd(new Date());

    const auditDocRef = doc(db, "venues", venueId, "audits", dayKey);
    const weekDocRef = doc(db, "venues", venueId, "auditWeeks", weekStartKey);

    await runTransaction(db, async (tx) => {
      const weekSnap = await tx.get(weekDocRef);

      let effectiveClosedDays = { ...closedDays.value };

      if (!weekSnap.exists()) {
        tx.set(weekDocRef, {
          venueId,
          weekStartKey,
          closedDays: { ...closedDays.value },
          createdAt: serverTimestamp(),
          createdByUid: user.uid,
        });
      } else {
        const data = weekSnap.data() || {};
        if (data.closedDays) effectiveClosedDays = { ...data.closedDays };
      }

      tx.set(auditDocRef, {
        venueId,
        dayKey,
        weekStartKey,

        questionSetId: activeAuditConfig.value.id,
        questionSetVenueId: activeAuditConfig.value.venueId,
        questionSetLabel: activeAuditConfig.value.label,

        venueScore: venueScore.value,
        adminScore: adminScore.value,

        answeredCount: answeredCount.value,
        totalCount: totalCount.value,

        answers: { ...answers.value },
        closedDays: { ...effectiveClosedDays },

        updatedAt: serverTimestamp(),
        updatedByUid: user.uid,
      });
    });

    console.log("[AUDIT] saved", {
      venueId,
      dayKey,
      weekStartKey,
      questionSetId: activeAuditConfig.value.id,
      venueScore: venueScore.value,
      adminScore: adminScore.value,
    });

    await loadWeeklyClosedDaysIfExists(venueId);
    showResults.value = true;
  } catch (e) {
    console.error("[AUDIT] submit failed:", e);
    errorMsg.value = e.message || "Submit failed";
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.venue-audit .card {
  border-radius: 18px;
}

.venue-audit .card-content {
  padding: 28px 32px;
}

.results-wrap {
  margin-top: 24px;
  padding: 10px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(120, 144, 156, 0.25);
}

.brand {
  border-radius: 12px;
}

.closed-days-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.closed-day-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(120, 144, 156, 0.25);
  border-radius: 999px;
  background: #fff;
}

.closed-day-chip.disabled {
  opacity: 0.65;
}

.section-progress-text {
  margin-left: 10px;
  font-size: 12px;
}

@media only screen and (max-width: 600px) {
  .row.valign-wrapper {
    display: block;
  }

  .card .card-content {
    padding: 14px !important;
  }

  .btn,
  .btn-small,
  button.btn,
  button.btn-small {
    width: 100%;
    margin: 4px 0;
  }

  .right-align {
    text-align: left !important;
  }

  table th,
  table td {
    white-space: nowrap;
  }

  .responsive-table,
  .table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .collapsible-header {
    flex-wrap: wrap;
    gap: 4px;
  }

  .section-progress-text {
    width: 100%;
    margin-left: 34px;
  }
}
</style>
